import React from 'react';
import { hasNpcTextMarkup, NPC_TEXT_ABILITY_REWARD, parseNpcText } from '../../../../shared/content/npcTextMarkup';
import { getAbilityData } from '../../../../shared/content/AbilityDataAdapter';
import { abilityIconPath, hasAbilityIcon } from '../../../rendering/abilities/AbilityIconRegistry';
import { ITEM_DATABASE } from '../../../../shared/ItemData';
import { useKeyBindings } from '../../hooks/useKeyBindings';
import { ItemIcon } from '../../components/ItemIcon';
import { InputHint } from '../../primitives/InputHint';
import { npcKeyLabel } from '../../npc/npcKeyLabel';
import { Tooltip, createItemTooltip } from '../../components/Tooltip';
import { useItemTooltipViewer } from '../../hooks/useItemTooltipViewer';

/**
 * An item named in an NPC line: its icon and name, inline. Hover or focus
 * shows the shared item tooltip with the compare-to-worn delta (plan 23 row 27).
 */
export const NpcItemChip: React.FC<{ itemId: string; quantity?: number }> = ({ itemId, quantity }) => {
  const viewer = useItemTooltipViewer();
  const name = ITEM_DATABASE[itemId]?.name ?? itemId;
  const chip = <span className="npc-text-item" data-npc-text-item={itemId} tabIndex={0}>
    <span className="npc-text-item-icon" aria-hidden="true"><ItemIcon itemId={itemId} size={20} /></span>
    <span className="npc-text-item-name">{name}{quantity !== undefined && quantity > 1 ? ` ×${quantity}` : ''}</span>
  </span>;
  if (!ITEM_DATABASE[itemId]) return chip;
  return <Tooltip inline data={createItemTooltip({ id: itemId, name }, viewer)}>{chip}</Tooltip>;
};

/** An ability named in an NPC line: its authored icon and name, inline. */
export const NpcAbilityChip: React.FC<{ abilityId: string }> = ({ abilityId }) => (
  <span className="npc-text-item npc-text-ability" data-npc-text-ability={abilityId}>
    {hasAbilityIcon(abilityId) && <img className="npc-text-item-icon" src={abilityIconPath(abilityId)} alt="" width={20} height={20} />}
    <span className="npc-text-item-name">{getAbilityData(abilityId)?.name ?? abilityId.replace(/_/g, ' ')}</span>
  </span>
);

const KeyGlyph: React.FC<{ id: string; label: string }> = ({ id, label }) => (
  <span className="npc-text-key" data-npc-text-key={id}>
    <InputHint label={label} device="keyboard" variant="parchment" title={`Keyboard: ${label}`} />
    <span className="npc-sr-only">{label}</span>
  </span>
);

/**
 * An authored NPC line with its `{item:…}` / `{key:…}` / `{ability:…}` markup
 * rendered as item and ability chips and live key glyphs (plan 13 §10).
 * `rewardAbilityId` fills `{ability:reward}`. Plain lines pass through.
 */
export const NpcRichText: React.FC<{ text: string; rewardAbilityId?: string }> = ({ text, rewardAbilityId }) => {
  const bindings = useKeyBindings();
  if (!hasNpcTextMarkup(text)) return <>{text}</>;
  return <>{parseNpcText(text).map((segment, index) => {
    if (segment.kind === 'text') return <React.Fragment key={index}>{segment.text}</React.Fragment>;
    if (segment.kind === 'item') return <NpcItemChip key={index} itemId={segment.id} />;
    if (segment.kind === 'ability') {
      // {ability:reward}: the ability this quest taught the reader's class.
      const abilityId = segment.id === NPC_TEXT_ABILITY_REWARD ? rewardAbilityId : segment.id;
      return abilityId ? <NpcAbilityChip key={index} abilityId={abilityId} />
        : <React.Fragment key={index}>your new ability</React.Fragment>;
    }
    const label = npcKeyLabel(segment.id, bindings);
    // An unbound action reads as its name rather than an empty chip.
    return label ? <KeyGlyph key={index} id={segment.id} label={label} />
      : <React.Fragment key={index}>{segment.id.replace(/_/g, ' ')}</React.Fragment>;
  })}</>;
};
